import localizeCurrency from "@helpers/localizeCurrency"
import localizeDate from "@helpers/localizeDate"
import { getPlanDetails } from "@helpers/plans"
import resolvePrice from "@helpers/pricing"
import { addDays, addMonths, addWeeks, addYears } from "date-fns"
import { Trans, useTranslation } from "react-i18next"
import { Link } from "react-router-dom"
import { Plan, StripePrice } from "src/types"

const APP_NAME = "VisualMind"

interface Props {
  className?: string
  currency: string
  plan: Plan
  stripeFullPrice: StripePrice | undefined
  stripeIntroPrice: StripePrice | undefined
}

function getIntroEndDate({
  interval,
  interval_count,
}: StripePrice["recurring"]): Date {
  const now = new Date()

  switch (interval) {
    case "day":
      return addDays(now, interval_count)
    case "week":
      return addWeeks(now, interval_count)
    case "month":
      return addMonths(now, interval_count)
    default:
      return addYears(now, interval_count)
  }
}

export default function SubscriptionTerms({
  className = "rounded-card bg-vm-surface p-5 text-vm-muted sm:p-6",
  currency,
  plan,
  stripeFullPrice,
  stripeIntroPrice,
}: Props) {
  const { t } = useTranslation()

  const introPrice = resolvePrice({ currency, stripePrice: stripeIntroPrice })
  const fullPrice = resolvePrice({ currency, stripePrice: stripeFullPrice })

  if (!introPrice || !fullPrice || !stripeIntroPrice?.recurring) return null

  const introEndDate = localizeDate(getIntroEndDate(stripeIntroPrice.recurring))

  return (
    <div className={`space-y-4 text-left text-sm leading-relaxed ${className}`}>
      <p>
        {t("pages.checkout.plans.subscriptionTerms.chargedToday", {
          price: localizeCurrency(introPrice),
        })}
      </p>

      <p>
        {t("pages.checkout.plans.subscriptionTerms.introPeriodEnds", {
          date: introEndDate,
        })}
      </p>

      <p>
        {t("pages.checkout.plans.subscriptionTerms.cancelBeforeRenewal", {
          date: introEndDate,
        })}
      </p>

      <p>
        {t(getPlanDetails(plan).autoRenewKey, {
          appName: APP_NAME,
          price: localizeCurrency(fullPrice),
        })}
      </p>

      <p>{t("pages.checkout.plans.subscriptionTerms.noPartialRefunds")}</p>

      <p>
        <Trans
          components={{
            privacyLink: (
              <Link
                className="underline"
                target="_blank"
                to="https://www.visualmind.app/privacy.html"
              />
            ),
            termsLink: (
              <Link
                className="underline"
                target="_blank"
                to="https://www.visualmind.app/terms.html"
              />
            ),
          }}
          i18nKey="pages.checkout.plans.subscriptionTerms.legalBinding"
        />
      </p>
    </div>
  )
}
